import React, { useState } from 'react';
import styled from 'styled-components';
import { Box, Divider } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useTranslation } from 'react-i18next';

import { IUserType } from 'graphql/types';
import { DetailRow } from 'common/components/DetailRow';
import { useSnackbarContext } from 'common/context';
import { ButtonLink } from 'common/components/buttons/ButtonLink';
import { ISnackbarState } from 'common/interfaces/snackbarState';
import { CrownIcon } from 'common/components/icons';

import { UserForm } from '../forms/UserForm';

interface IProps {
    className?: string;
    divider?: boolean;
    user: Pick<
        IUserType,
        | 'id'
        | 'firstName'
        | 'lastNamePrefix'
        | 'lastName'
        | 'email'
        | 'jobTitle'
        | 'phone'
        | 'isMainUser'
        | 'locale'
    >;
    onDeleteUser?(snackbarState?: ISnackbarState): void;
}

const BaseUserInfoSection = ({
    className,
    divider,
    user,
    onDeleteUser,
}: IProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const { setSnackbarState } = useSnackbarContext();
    const { t } = useTranslation();

    return (
        <>
            {isEditing ? (
                <UserForm
                    canDelete={!!onDeleteUser}
                    user={user}
                    onClose={(snackbarState, refetch) => {
                        if (!onDeleteUser && snackbarState) {
                            setSnackbarState(snackbarState);

                            if (snackbarState.variant === 'error') return;
                        }

                        if (refetch && onDeleteUser) {
                            onDeleteUser(snackbarState);
                        }

                        setIsEditing(false);
                    }}
                />
            ) : (
                <Box className={className}>
                    <Box>
                        <Box display="flex" mb={2}>
                            <DetailRow bold description={t('user.name')}>
                                {`${user.firstName} ${user.lastNamePrefix} ${user.lastName}`}
                            </DetailRow>

                            {user.isMainUser && (
                                <Box ml="auto" position="relative" top="-8px">
                                    <CrownIcon />
                                </Box>
                            )}
                        </Box>
                        <DetailRow description={t('formfields.jobTitle')}>
                            {user.jobTitle}
                        </DetailRow>
                        <DetailRow description={t('formfields.email')}>
                            {user.email}
                        </DetailRow>
                        <DetailRow description={t('formfields.phone')}>
                            {user.phone}
                        </DetailRow>
                    </Box>
                    <Box mt={6}>
                        <ButtonLink
                            icon={<ArrowForwardIcon />}
                            onClick={() => setIsEditing(true)}
                        >
                            {t('user.editData')}
                        </ButtonLink>
                    </Box>
                </Box>
            )}
            {divider && <Divider className="divider" />}
        </>
    );
};

export const UserInfoSection = styled(BaseUserInfoSection)`
    .divider {
        margin: ${({ theme }) => `${theme.spacing(4)}px 0`};
    }

    ${DetailRow}:last-child {
        margin-bottom: 0;
    }
`;
